import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ZnSelectQueryParams } from '../generic-form/interfaces/IGenericFieldOptions';

export type ZnSelectOptionsLoader = (
  params?: ZnSelectQueryParams
) => Observable<
  { key: string; value: string }[] | { items: { key: string; value: string }[]; totalCount?: number }
>;

export type ZnSelectLoaderContext = {
  getLoader: () => ZnSelectOptionsLoader | undefined;
  getPageSize: () => number;
  getSkipCount: () => number;
  setSkipCount: (value: number) => void;
  getSearch: () => string;
  isDropdownOpen: () => boolean;
  getOptionsContainer: () => HTMLElement | undefined;
  setLoading: (value: boolean) => void;
  setLoadingMore: (value: boolean) => void;
  onOptionsLoaded: (
    payload: { key: string; value: string }[],
    total: number | null,
    append: boolean,
    previousScrollTop: number
  ) => void;
  onLoadError: (error: unknown) => void;
  getAsyncOptionsLength: () => number;
  getTotalCount: () => number | null;
  setTotalCount: (value: number | null) => void;
  setLastBatchSize: (value: number) => void;
  getLastBatchSize: () => number;
  onResetOptions: () => void;
};

export class ZnSelectLoader {
  private loadSubscription?: Subscription;
  private searchDebounce$ = new Subject<string>();
  private searchDebounceSub?: Subscription;

  constructor(private ctx: ZnSelectLoaderContext) {
    this.searchDebounceSub = this.searchDebounce$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(term => {
        if (!this.ctx.getLoader() || !this.ctx.isDropdownOpen()) {
          return;
        }
        this.resetPagination();
        this.loadOptions({ search: term });
      });
  }

  destroy(): void {
    this.loadSubscription?.unsubscribe();
    this.searchDebounceSub?.unsubscribe();
  }

  queueSearch(term: string): void {
    this.searchDebounce$.next(term);
  }

  loadOptions(params?: ZnSelectQueryParams, append: boolean = false): void {
    this.loadSubscription?.unsubscribe();

    const loader = this.ctx.getLoader();
    if (!loader) return;

    if (append) {
      this.ctx.setLoadingMore(true);
    } else {
      this.ctx.setLoading(true);
    }

    const normalizedParams: ZnSelectQueryParams = {
      ...params,
      skipCount: params?.skipCount ?? this.ctx.getSkipCount(),
      maxResultCount: params?.maxResultCount ?? this.ctx.getPageSize(),
    };
    this.ctx.setSkipCount(normalizedParams.skipCount ?? 0);

    const previousScrollTop = append ? this.ctx.getOptionsContainer()?.scrollTop ?? 0 : 0;

    this.loadSubscription = loader(normalizedParams).subscribe({
      next: (response) => {
        const payload = Array.isArray(response) ? response : response.items;
        const total = Array.isArray(response) ? null : response.totalCount ?? null;
        this.ctx.setLastBatchSize(payload.length);

        if (append && payload.length === 0) {
          if (this.ctx.getTotalCount() === null) {
            this.ctx.setTotalCount(this.ctx.getAsyncOptionsLength());
          }
          this.ctx.setLoading(false);
          this.ctx.setLoadingMore(false);
          return;
        }

        this.ctx.onOptionsLoaded(payload, total, append, previousScrollTop);
        this.ctx.setLoading(false);
        this.ctx.setLoadingMore(false);
      },
      error: error => {
        this.ctx.onLoadError(error);
        this.ctx.setLoading(false);
        this.ctx.setLoadingMore(false);
      }
    });
  }

  resetPagination(): void {
    this.ctx.setSkipCount(0);
    this.ctx.setTotalCount(null);
    this.ctx.setLastBatchSize(0);
    this.ctx.onResetOptions();
  }

  hasMore(): boolean {
    const total = this.ctx.getTotalCount();
    if (total === null) {
      return this.ctx.getLastBatchSize() === this.ctx.getPageSize();
    }
    return this.ctx.getAsyncOptionsLength() < total;
  }

  loadNextPage(): void {
    if (!this.hasMore()) return;
    this.ctx.setSkipCount(this.ctx.getSkipCount() + this.ctx.getPageSize());
    this.loadOptions({ search: this.ctx.getSearch() }, true);
  }
}
